import { useEffect, useState, useContext, useRef } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import UserDashboard from "./components/dashboard";
import { LoginService, OpenAPI } from "@propertelligent/client-api";
import { ForgetPassword } from "./components/user/ForgetPassword";
import CheckEmail from "./components/user/CheckEmail";
import { GeneratePdfDocument } from "./components/generatePdfDocument";
import ResetPassword from "./components/user/ResetPassword";
import { MyContext } from "./utils/context/LoginContext";
import { UserLoginData } from "./components/common/charts/Enums";
import { Login } from "./components/user/Login";
import Signup from "./components/user/Signup";
import { MAIN_ROUTES_URLS, URLS, USER_ROLES, USERS } from "./constants/constants";
import { useDispatch } from "react-redux";
import GmailAuthReturn from "./components/Messaging/Email/components/GmailAuthRetrun";
import OutlookAuthReturn from "./components/Messaging/Email/components/OutlookAuthReturn";
import NewQuestionnaire from "./components/user/NewQuestionnaire";
import CustomMenu from "./layout/CustomMenu";
import CustomTopBar from "./layout/CustomTopBar";
import { MenuContext } from "./utils/context/MenuContext";
import { NotificationPayload, NotificationType, PubSub, TopicsEnum } from "./utils/pubsub";
import Notification from "./components/common/Notification/Notification";
import { GenerateEmail } from "./components/generateEmail";
import { MyOrganization } from "./components/my-organization";
import TenantOnboard from "./components/TenantOnboard";
import InviteUser from "./components/user/inviteUser";
import { PlatformSettings } from "./components/platform-settings";
import { MySettings } from "./components/my-settings";
import ServerErrorPopup from "./newComponents/errorPopup/server-error-popup";
import MaintenanceBannerOverlay from "./newComponents/maintenance-banner/MaintenanceBannerOverlay";
import Dashboard from "./tenantComponent/components/tenantDashboard";
import ContractorDashboard from "./contractorComponent/components/contractorDashboard";
import { SetPassword } from "./components/user/SetPassword";
import ChatbotWidget from "./newComponents/chatbot/ChatbotWidget";
import { ChatbotProvider } from "./utils/context/ChatbotContext";
import PlatformMenu from "./layout/PlatformMenu";
import { PlatformOrgProvider } from "./utils/context/PlatformOrgContext";
import PlatformPortalRoutes from "./components/platform-portal/PlatformPortalRoutes";

export default function App(): JSX.Element | any {
  const [showNotification, setShowNotification] = useState(false);
  const [isGenerateView, setIsGenerateView] = useState(false);
  const [notificationPayload, setNotificationPayload] = useState<NotificationPayload>({ type: NotificationType.None, message: "", title: "", icon: undefined });
  const [showServerError, setShowServerError] = useState(false);
  const { isLogin, setIsLogin, userRole } = useContext(MyContext);
  const { isOpen, setIsOpen, isHidden, setIsHidden } = useContext(MenuContext);
  const location = useLocation();
  const userRoleId = sessionStorage.getItem("userRoleId");
  const userOnboarded = sessionStorage.getItem("userOnboarded");
  const tenantData = JSON.parse(sessionStorage.getItem("tenantInfo"));
  
  const size = window.innerWidth;
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const loginToken = sessionStorage.getItem(UserLoginData.token);

  // Scroll to top whenever route changes
useEffect(() => {
  // Scroll window
  window.scrollTo(0, 0);

  // Scroll all scrollable elements
  const allScrollable = document.querySelectorAll('*');

  allScrollable.forEach((el) => {
    if (el.scrollTop) {
      el.scrollTop = 0;
    }
  });
}, [location.pathname]);


  const triggerNotification = (data: NotificationPayload) => {
    setNotificationPayload(data);
    setShowNotification(true);
    window.setTimeout(() => {
      setShowNotification(false);
    }, 4000);
  };

  useEffect(() => {
    const currentLink = window.location.href;

    if (currentLink.includes("generatePdfDocument") || currentLink.includes("generateEmail")) {
      setIsGenerateView(true);
    }

    PubSub.subscribe(
      TopicsEnum.Notification.toString(),
      (data: NotificationPayload) => {
        triggerNotification(data);
      }
    );

    PubSub.subscribe(
      TopicsEnum.Logout.toString(),
      (data: NotificationPayload) => {
        LoginService.putApiLoginLogout();
        triggerNotification(data);
        sessionStorage.clear();
        setIsLogin(false);
        setTimeout(() => {
          navigate(URLS.LOGIN, {
            state: { isComingFromLogout: true },
          });
          navigate(URLS.HOME);
        }, 5000);
      }
    );

    PubSub.subscribe(
      TopicsEnum.ServerError.toString(),
      (data: NotificationPayload) => {
        setShowServerError(true);
      }
    );
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsHidden(window.innerWidth < 400);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [setIsHidden]);

  useEffect(() => {
    setIsLogin(loginToken);
    OpenAPI.TOKEN = loginToken;
  }, [loginToken, setIsLogin]);

  useEffect(() => {
    if (location.pathname === MAIN_ROUTES_URLS.ORGANIZATION || location.pathname === MAIN_ROUTES_URLS.PLATFORMSETTINGS) {
      setIsHidden(true);
    } else {
      setIsHidden(size < 400);
    }
  }, [location.pathname, setIsHidden, size]);

  const cleanPathname = location.pathname.replace(/^\/+|\/+$/g, "");
  const isOrganizationPath = cleanPathname.startsWith("organization");
  const isPlatformSettingsPath = cleanPathname.startsWith("platform-settings");
  const isMySettingsPath = cleanPathname.startsWith("my-settings");
  const generatePdf = cleanPathname.startsWith("generatePdfDocument");
  return (
    <div className="App">
      {isLogin && isLogin ? (
        <>
          {USERS.USER === USER_ROLES[userRoleId] && (
            <>
              {generatePdf ? (
                <Routes>
                  <Route path={URLS.GENERATE_PDF_DOCUMENT} element={<GeneratePdfDocument />} />
                </Routes>
              ) : (
                <ChatbotProvider>
                  {(userOnboarded === "true") && (<CustomTopBar size={size} userRoleId={userRoleId} showUserName={true} />)}
                  <div className="menuBarContainer">
                    {userOnboarded === "true" && (
                      <div ref={menuRef}
                        className={isHidden ? "hiddenMenu" : isOpen
                          ? (isOrganizationPath || isPlatformSettingsPath || isMySettingsPath)
                            ? "openedMenuOrganization"
                            : "openedMenu"
                          : "closedMenu"
                        } >
                        {!isOrganizationPath && !isPlatformSettingsPath && !isMySettingsPath && (
                          <CustomMenu size={size} userRoleId={parseInt(userRoleId)} />
                        )}
                        {isOrganizationPath && <MyOrganization />}
                        {isPlatformSettingsPath && <PlatformSettings />}
                        {isMySettingsPath && <MySettings />}
                      </div>
                    )}
                    <div className={`${userOnboarded === "false" ? "hiddenMenuRightSide" : isOpen
                      ? (isOrganizationPath || isPlatformSettingsPath || isMySettingsPath)
                        ? "openedMenuFullWidth"
                        : "openedMenuRightSide"
                      : "closeMenuRightSide"
                      }`}
                    >
                      <div className="app-container">
                        <Routes>
                          <Route path={URLS.USER_DASHBOARD} element={<UserDashboard userRoleId={parseInt(userRoleId)} />} />
                          <Route path="/callback/gmail" element={<GmailAuthReturn />} />
                          <Route path="/callback/outlook" element={<OutlookAuthReturn />} />
                          <Route path={MAIN_ROUTES_URLS.HOME} element={userOnboarded === "false" ? <NewQuestionnaire /> :
                            <UserDashboard userRoleId={parseInt(userRoleId)} />} />
                          <Route path={URLS.QUESTIONNAIRE} element={<NewQuestionnaire />} />
                          {isOrganizationPath && (
                            <Route path={MAIN_ROUTES_URLS.ORGANIZATION} element={<MyOrganization />} />
                          )}
                          {isPlatformSettingsPath && (
                            <Route path={MAIN_ROUTES_URLS.PLATFORMSETTINGS} element={<PlatformSettings />} />
                          )}
                          {isMySettingsPath && (
                            <Route path={MAIN_ROUTES_URLS.MY_SETTINGS} element={<MySettings />} />
                          )}
                        </Routes>
                      </div>
                    </div>
                  </div>
                  {/* AI assistant — org users only, launched from the header; hidden on onboarding/PDF/login views. */}
                  {userOnboarded === "true" && <ChatbotWidget />}
                </ChatbotProvider>
              )}
            </>
          )}
          {USERS.TENANT === USER_ROLES[userRoleId] && (
            <>
              {!tenantData?.unitId ? (
                <Routes>
                  <Route path={MAIN_ROUTES_URLS.HOME} element={<TenantOnboard userRoleId={parseInt(userRoleId)} />} />
                </Routes>
              ) : (
                <div className="menuBarContainer" style={{ display: "flex" }}>
                  <div className={isOpen ? "openedMenuRightSide" : "closeMenuRightSide"} >
                    <div className="app-container">
                      <Routes>
                        <Route path={MAIN_ROUTES_URLS.MY_SETTINGS} element={<MySettings />} />
                        <Route path={MAIN_ROUTES_URLS.HOME} element={<Dashboard />} />
                      </Routes>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          {USERS.CONTRACTOR === USER_ROLES[userRoleId] && (
            <div className="menuBarContainer" style={{ display: "flex" }}>
              <div className={isOpen ? "openedMenuRightSide" : "closeMenuRightSide"} >
                <div className="app-container">
                  <Routes>
                    <Route path={MAIN_ROUTES_URLS.MY_SETTINGS} element={<MySettings />} />
                    <Route path={MAIN_ROUTES_URLS.HOME} element={<ContractorDashboard />} />
                  </Routes>
                </div>
              </div>
            </div>
          )}
          {/* Platform super-admin portal. Gated purely by role (a Platform JWT carries no
              per-module permission map). No ChatbotProvider/Widget for platform. */}
          {/* Platform super-admin portal. The header org-switcher picks which organization the portal acts as;
              the whole portal then mimics that org's landlord portal (reused org menu + pages) EXCEPT the home
              dashboard (which stays the platform-specific PlatformDashboard). Platform-only surfaces live under
              the "Platform Settings" sub-shell. Gated purely by role (a Platform JWT has no permission map). */}
          {USERS.PLATFORM === USER_ROLES[userRoleId] && (
            <PlatformOrgProvider>
              <CustomTopBar size={size} userRoleId={userRoleId} showUserName={true} />
              <div className="menuBarContainer">
                <div
                  ref={menuRef}
                  className={isHidden ? "hiddenMenu" : isOpen ? "openedMenu" : "closedMenu"}
                >
                  {location.pathname.startsWith(URLS.PLATFORM_SETTINGS) ? (
                    <PlatformMenu size={size} />
                  ) : (
                    <CustomMenu size={size} userRoleId={1} hideCommunication={true} />
                  )}
                </div>
                <div className={isOpen ? "openedMenuRightSide" : "closeMenuRightSide"}>
                  <div className="app-container">
                    <PlatformPortalRoutes />
                  </div>
                </div>
              </div>
            </PlatformOrgProvider>
          )}
        </>
      ) : (
        <Routes>
          <Route path={MAIN_ROUTES_URLS.HOME} element={<Login />} />
          <Route path={MAIN_ROUTES_URLS.TENANT_ONBOARD} element={<TenantOnboard />} />
          <Route path={MAIN_ROUTES_URLS.INVITE_USER} element={<InviteUser />} />
          <Route path={URLS.RESET_PASSWORD_BY_USERKEY} element={<ResetPassword />} />
          <Route path={URLS.SETUP_PASSWORD_BY_USERKEY} element={<ResetPassword />} />
          <Route path={URLS.FORGET_PASSWORD} element={<ForgetPassword />} />
          <Route path={URLS.CHECK_MAIL} element={<CheckEmail />} />
          <Route path={URLS.SIGN_UP} element={<Signup />} />
          <Route path={URLS.SET_PASSWORD} element={<SetPassword />} />
          <Route path={URLS.GENERATE_EMAIL} element={<GenerateEmail />} />
        </Routes>
      )}
      {showNotification && (
        <Notification
          payload={notificationPayload}
          setNotification={(value) => {
            setShowNotification(value);
          }}
        />
      )}
      <ServerErrorPopup
        isOpen={showServerError}
        onClose={() => setShowServerError(false)}
      />
      <MaintenanceBannerOverlay />
    </div>
  );
}